import React, { useEffect, useRef, useState } from "react";
import {
  Date,
  InputWrap,
  DocumentInputWrap,
  StyledWrapProps,
  WrapStyle,
} from "./styles";
import { DateIcon } from "assets/icons";
import Calendar from "../Calendar";
import { DATE_FORMAT } from "config/constant";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import dayjs from "dayjs";
import { ComponentUsageType } from "models/document/ComponentUsageType";
import DocumentTextField from "../DocumentTextField/DocumentTextField";
import { RangeDateType } from "models/DateTypes";

interface RangeDateStringType {
  start: string;
  end: string;
}

interface DateRangePickerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "defaultValue">,
    StyledWrapProps {
  usage?: ComponentUsageType;
  dateFormat?: string;
  inputComponent?: React.ReactNode;
  defaultValue?: RangeDateStringType;
  value?: RangeDateStringType;
  placeholder?: { start?: string; end?: string };
  isTimePicker?: boolean;
  errorMessage?: { start?: string; end?: string };
  onChangeDate?: (date: RangeDateType, dateString: RangeDateStringType) => void;
  onClickReset?: boolean;
}

/**
 * @param {function} onChangeDate DatePicker로 특정 날짜를 선택하면 해당 날짜를 인자로 반환합니다.
 */
const DateRangePicker = ({
  usage,
  dateFormat = DATE_FORMAT,
  disabled,
  inputComponent,
  defaultValue,
  value,
  placeholder,
  isTimePicker,
  errorMessage,
  onChangeDate,
  onClickReset,
  ...rest
}: DateRangePickerProps) => {
  const isValidDate = defaultValue
    ? defaultValue &&
      dayjs(defaultValue.start).isValid() &&
      dayjs(defaultValue.end).isValid()
    : false;

  const datePickerRef = useRef<HTMLDivElement>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<
    RangeDateType | undefined
  >(
    isValidDate && defaultValue
      ? {
          start: dayjs(dayjs(defaultValue.start).format(dateFormat)).toDate(),
          end: dayjs(dayjs(defaultValue.end).format(dateFormat)).toDate(),
        }
      : undefined,
  );

  const [startDate, setStartDate] = useState<string>(() =>
    isValidDate ? dayjs(defaultValue?.start).format(dateFormat) : "",
  );

  const [endDate, setEndDate] = useState<string>(() =>
    isValidDate ? dayjs(defaultValue?.end).format(dateFormat) : "",
  );

  /**
   * @description DatePicker의 다른 날짜를 선택했을 때의 이벤트 핸들러
   */
  const handleChangeDate = (selectedRange: RangeDateType) => {
    setSelectedDateRange(selectedRange);

    const newStartDate = selectedRange?.start
      ? dayjs(selectedRange.start).format(dateFormat)
      : "";
    setStartDate(newStartDate);

    const newEndDate = selectedRange?.end
      ? dayjs(selectedRange.end).format(dateFormat)
      : "";
    setEndDate(newEndDate);

    if (onChangeDate)
      onChangeDate(selectedRange, {
        start: newStartDate,
        end: newEndDate,
      });
  };

  useEffect(() => {
    if (onClickReset !== undefined) {
      setSelectedDateRange(undefined);
      setStartDate("");
      setEndDate("");
    }
  }, [onClickReset]);

  const [open, setOpen] = useState(false);
  const onCloseCalendar = () => setOpen(false);

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <WrapStyle ref={datePickerRef} {...rest}>
        <DropdownMenu.Trigger asChild>
          {usage !== "document" ? (
            <InputWrap filled={!!selectedDateRange} disabled={disabled}>
              <Date isPlaceholder={!!placeholder?.start}>
                {value
                  ? value.start
                  : startDate || placeholder?.start || "날짜 추가"}
              </Date>
              <p>~</p>
              <Date isPlaceholder={!!placeholder?.start}>
                {value ? value.end : endDate || placeholder?.end || "날짜 추가"}
              </Date>
              <DateIcon />
            </InputWrap>
          ) : (
            <DocumentInputWrap>
              <DocumentTextField
                leftIcon={<DateIcon />}
                placeholder={placeholder?.start}
                value={value ? value.start : startDate}
                errorMessage={errorMessage?.start}
              />
              <p>~</p>
              <DocumentTextField
                leftIcon={<DateIcon />}
                placeholder={placeholder?.end}
                value={value ? value.end : endDate}
                errorMessage={errorMessage?.end}
              />
            </DocumentInputWrap>
          )}
        </DropdownMenu.Trigger>
      </WrapStyle>

      <DropdownMenu.Portal>
        <DropdownMenu.Content sideOffset={5}>
          <Calendar
            type="range"
            rangeDate={selectedDateRange}
            isTimePicker={isTimePicker}
            onChangeRangeDate={handleChangeDate}
            onCloseCalendar={onCloseCalendar}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default DateRangePicker;
